<template>
  <Box>
    <BoxHead icon="fas fa-car-crash">
      Insurance
    </BoxHead>

    <BoxLoader v-if="loading" />

    <EmptySection
      v-else-if="isEmpty"
      :badges="['Insurance']"
    />

    <template
      v-else
    >
      <Callout
        v-if="hasPolicyExpiringSoon"
        status="danger"
        icon-before="fas fa-exclamation"
      >
        RENEWAL NEEDED
      </Callout>

      <DataTable
        :head="insuranceTable.head"
        :body="insuranceTable.body"
        :foot="insuranceTable.foot"
      />

      <DetailsTitle
        v-if="insurance?.total_annual_premium"
        icon="fas fa-money-bill"
        :value="formatCurrency(insurance.total_annual_premium)"
      >
        Total Annual Premium
      </DetailsTitle>

      <Stack
        v-if="(insurance.coverage_gaps && insurance.coverage_gaps.length > 0 ||
          insurance.overlapping_coverage && insurance.overlapping_coverage.length > 0) &&
          recommendations.length === 0"
        gap="2"
      >
        <DetailsTitle>
          Coverage Analysis
        </DetailsTitle>

        <!-- Coverage Gaps -->
        <Callout
          v-for="(gap, index) in insurance.coverage_gaps || []"
          :key="'gap-' + index"
          icon="fas fa-exclamation-triangle"
          status="danger"
        >
          {{ gap }}
        </Callout>

        <!-- Overlapping Coverage -->
        <Callout
          v-for="(overlap, index) in insurance.overlapping_coverage || []"
          :key="'overlap-' + index"
          icon="fas fa-copy"
          status="warning"
        >
          {{ overlap }}
        </Callout>
      </Stack>
    </template>
  </Box>
</template>

<script setup>
import { computed } from 'vue';
import { Box, BoxHead, BoxLoader, Callout, DataTable, DetailsTitle, Stack } from '@stakeui';
import { formatCurrency } from '@frontend/shared/Util.js';
import EmptySection from './EmptySection.vue';
import { isEmptyValue } from '@frontend/shared/Util.js';

const props = defineProps({
  client: {
    type: Object,
    default: null,
  },
  loading: {
    type: Boolean,
    default: false,
  },
  recommendations: {
    type: Array,
    default: () => ([]),
  },
});

const insurance = computed(() => props.client?.insurance);
const isEmpty = computed(() => isEmptyValue(insurance.value));

const hasPolicyExpiringSoon = computed(() =>
  props.client?.insurance?.policies?.some(policy => policy.alert === 'EXPIRES SOON') ?? false,
);

const insuranceTable = computed(() => {
  if (!insurance?.value) {
    return {};
  }

  const body = insurance.value.policies.map((policy, index) => {
    let alertMessage = '';

    if (policy.alert) {
      alertMessage = `${policy.alert} `;
    }

    if (policy.expiryDate) {
      alertMessage += `Exp: ${policy.expiryDate}`;
    }

    return {
      id: 'checking',
      cells: [
        { id: 'type', label: policy.type },
        { id: 'provider', label: policy.provider },
        { id: 'coverage', label: policy.coverage },
        { id: 'insured', label: policy.insured },
        { id: 'alerts', label: alertMessage },
        { id: 'value', label: formatCurrency(policy.premium), type: 'numeric' },
      ],
    };
  });

  return {
    head: [
      { id: 'type', label: 'Type' },
      { id: 'provider', label: 'Provider' },
      { id: 'coverage', label: 'Coverage' },
      { id: 'insured', label: 'Insured' },
      { id: 'alerts', label: 'Alerts' },
      { id: 'premium', label: 'Premium' },
    ],
    body,
  };
});
</script>
